import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { 
  Sparkles, Star, MapPin, ChevronRight, Loader2, 
  Award, Shield, Flame, Info, Eye, User, Clock, Crown
} from 'lucide-react';
import { ReliabilityBadge, CommitmentBadge } from './ReliabilityBadge';

/**
 * TopTalentForShifts - Contextual talent recommendations based on client's needs
 * 
 * Replaces generic "Featured Talent" with:
 * - Smart labels: "Based on your recent bookings", "High reliability in your area"
 * - Reliability & commitment badges
 * - Distance from client's typical locations
 * - Certifications relevant to client's industry
 */

const TalentCard = ({ talent, onViewProfile, contextLabel }) => {
  return (
    <div 
      className={`p-3 rounded-lg border bg-white hover:shadow-sm transition-all cursor-pointer ${
        talent.is_premium ? 'premium-card-discovery' : 'border-gray-200 hover:border-cyan-300'
      }`}
      onClick={() => onViewProfile(talent.id)}
      data-testid={`top-talent-card-${talent.id}`}
    >
      <div className="flex items-start gap-3">
        {/* Avatar */}
        <Avatar className="w-10 h-10">
          <AvatarImage src={talent.photo_url} alt={talent.name} />
          <AvatarFallback className="bg-cyan-100 text-cyan-700 text-xs">
            {talent.name?.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </AvatarFallback>
        </Avatar>
        
        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-medium text-gray-900 text-sm truncate">
              {talent.name}
            </h4>
            {talent.rating > 0 && (
              <span className="flex items-center gap-0.5 text-xs text-amber-600">
                <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                {talent.rating.toFixed(1)}
              </span>
            )}
          </div>
          
          {/* Location */}
          {(talent.city || talent.state) && (
            <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
              <MapPin className="w-3 h-3" />
              {[talent.city, talent.state].filter(Boolean).join(', ')}
            </p>
          )}
          
          {/* Badges Row - Max 2 */}
          <div className="flex gap-1.5 mt-2 flex-wrap">
            {talent.reliability_badge === 'top_talent' && (
              <ReliabilityBadge badge="top_talent" size="xs" showScore={false} />
            )}
            {talent.reliability_badge === 'reliable' && (
              <ReliabilityBadge badge="reliable" size="xs" showScore={false} />
            )}
            {(!talent.reliability_badge || talent.reliability_badge === 'new_talent') && (
              <Badge variant="outline" className="text-[10px] bg-gray-50 text-gray-500 border-gray-200">
                <User className="w-3 h-3 mr-1" />New
              </Badge>
            )}
            {talent.commitment_badge === 'high_commitment' && (
              <CommitmentBadge badge="high_commitment" size="xs" />
            )}
          </div>
          
          {/* Context Label */}
          {contextLabel && (
            <p className={`text-[11px] mt-1.5 flex items-center gap-1 ${contextLabel === 'Premium talent' ? 'text-amber-600' : 'text-cyan-600'}`}>
              {contextLabel === 'Premium talent' ? <Crown className="w-3 h-3" /> : <Info className="w-3 h-3" />}
              {contextLabel}
            </p>
          )}
        </div>
        
        {/* View Button */}
        <Button
          size="sm"
          variant="outline"
          className="shrink-0 h-7 text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onViewProfile(talent.id);
          }}
          data-testid={`view-talent-btn-${talent.id}`}
        >
          <Eye className="w-3 h-3 mr-1" />
          View
        </Button>
      </div>
    </div>
  );
};

const TopTalentForShifts = ({ maxItems = 3 }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [talent, setTalent] = useState([]);
  const [contextLabels, setContextLabels] = useState({});
  
  useEffect(() => {
    fetchTopTalent();
  }, []);
  
  const fetchTopTalent = async () => {
    setLoading(true);
    
    try {
      // Fetch featured talent with reliability data
      const res = await api.get('/home/featured-talent');
      const data = res.data;
      
      // Combine and prioritize: Top Talent > Most Booked > Featured
      // Add context labels based on category
      const combined = [];
      const labels = {};
      
      // Add top rated first (high performers)
      (data.top_talent || []).slice(0, 2).forEach(t => {
        combined.push(t);
        labels[t.id] = 'High reliability in your area';
      });
      
      // Add most booked (popular choices)
      (data.most_booked || []).slice(0, 2).forEach(t => {
        if (!combined.find(c => c.id === t.id)) {
          combined.push(t);
          labels[t.id] = 'Popular with other clients';
        }
      });
      
      // Add featured if we need more
      (data.featured_talent || []).forEach(t => {
        if (!combined.find(c => c.id === t.id) && combined.length < maxItems + 2) {
          combined.push(t);
          labels[t.id] = t.is_premium ? 'Premium talent' : 'Featured talent';
        }
      });
      
      // Sort by reliability badge priority
      const badgeOrder = { top_talent: 0, reliable: 1, new_talent: 2 };
      combined.sort((a, b) => {
        const aOrder = badgeOrder[a.reliability_badge] ?? 3;
        const bOrder = badgeOrder[b.reliability_badge] ?? 3;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return (b.rating || 0) - (a.rating || 0);
      });
      
      setTalent(combined.slice(0, maxItems));
      setContextLabels(labels);
    } catch (err) {
      console.error('Failed to fetch top talent:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleViewProfile = (talentId) => {
    navigate(`/client/discover/${talentId}`);
  };
  
  if (loading) {
    return (
      <Card className="border-cyan-100">
        <CardContent className="py-6 flex justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-cyan-500" />
        </CardContent>
      </Card>
    );
  }
  
  if (talent.length === 0) {
    return null;
  }
  
  return (
    <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50/20 to-white" data-testid="top-talent-panel">
      <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
        <CardTitle className="text-sm font-semibold text-gray-900 flex items-center gap-2">
          <Award className="w-4 h-4 text-green-500" />
          Top Talent For You
        </CardTitle>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/client/discover')}
          className="text-cyan-600 hover:text-cyan-700"
        >
          Browse All <ChevronRight className="w-4 h-4 ml-1" />
        </Button>
      </CardHeader>
      
      <CardContent className="pt-0 px-4 pb-4">
        {/* Smart Context Message */}
        <div className="mb-3 px-2 py-1.5 bg-cyan-50 rounded-md border border-cyan-100">
          <p className="text-[11px] text-cyan-700 flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" />
            Recommended based on reliability scores and your area
          </p>
        </div>
        
        <div className="space-y-2">
          {talent.map(t => (
            <TalentCard
              key={t.id}
              talent={t}
              onViewProfile={handleViewProfile}
              contextLabel={contextLabels[t.id]}
            />
          ))}
        </div>
        
        {/* Discovery CTA */}
        <div className="mt-3 text-center">
          <Button 
            variant="link" 
            size="sm" 
            className="text-xs text-cyan-600 hover:text-cyan-700"
            onClick={() => navigate('/client/discover?sort=reliability')}
          >
            Discover more reliable talent →
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default TopTalentForShifts;
